import { Badge, Button, Card, CardBody, CodeDisplay, DataTable, FilterPanel, highlightText, Icon, matchesExactKey } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { FilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useHistory, useLocation, NavLink } from 'react-router-dom';
import { useSpinDelay } from 'spin-delay';
import {
  Applicant,
  ApplicantStatus,
  useApplicantsContext,
} from '../../../Contexts/ApplicantsContext';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import { useLocalizedStrings } from '../../../Localization';
import { useLocalFilterPanel, useSettingsFilterFields } from '../../Filters';
import { useFieldValueSuggestions } from '../../shared/hooks/useFieldValueSuggestions';
import { hasAnyFilters } from '../../shared/utils/filters';

export const ApplicantsDataTable = () => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const location = useLocation();

  const {
    queryParams,
    setQueryParams,
    applicantsData,
    isApplicantsLoading,
    fetchApplicants
  } = useApplicantsContext();

  // Filter panel setup
  const availableFields = useSettingsFilterFields();
  const { filterPanelProps, filterConfig, setFilterConfig, filterRef } = useLocalFilterPanel('all-applicants', {
    availableFields,
    enableFlushOnMount: true
  });
  const { fetchSuggestions } = useFieldValueSuggestions();

  // Track fetching state separately for spinner
  const [isFetching, setIsFetching] = useState(false);

  // Handle filter changes - update local state, reset page, and update context
  const handleFilterChange = useCallback((newFilterConfig: FilterConfig) => {
    // Update local filter state (for UI)
    setFilterConfig(newFilterConfig);

    // Set fetching state immediately to prevent empty state flash
    setIsFetching(true);

    // Update context with normalized filters (for API)
    const hasComplete = hasAnyFilters(newFilterConfig);
    const normalizedFilters = (newFilterConfig.enabled && hasComplete) ? newFilterConfig : undefined;

    setQueryParams(prev => ({
      ...prev,
      page: 1,
      viewFilters: normalizedFilters
    }));
  }, [setFilterConfig, setQueryParams]);

  // Track display value for search input (for immediate UI feedback)
  const [searchDisplay, setSearchDisplay] = useState(queryParams.search || '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if initial data has been loaded
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Use spin-delay to prevent loading flashes - only show loading after 250ms
  const showLoadingSpinner = useSpinDelay(isFetching && hasInitialLoad, {
    delay: 250,
    minDuration: 150
  });

  // Fetch applicants when query params change
  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      await fetchApplicants(queryParams);
      setIsFetching(false);
      if (!hasInitialLoad) {
        setHasInitialLoad(true);
      }
    };
    fetchData();
  }, [queryParams]);

  // URL parameter utilities
  const getUrlSearchParam = (key: string): string => {
    const params = new URLSearchParams(location.search);
    return params.get(key) || '';
  };

  const setUrlSearchParam = useCallback((key: string, value: string) => {
    const params = new URLSearchParams(location.search);
    if (value.trim()) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  // Enhanced search change handler with URL encoding and debouncing
  const handleSearchChange = useCallback((search: string) => {
    setSearchDisplay(search); // Update display immediately
    setUrlSearchParam('search', search); // Update URL immediately

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the query param update (which triggers the API call)
    const timeoutId = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        page: 1, // Reset to first page on search
        search
      }));
    }, 400);

    searchTimeoutRef.current = timeoutId;
  }, [setUrlSearchParam, setQueryParams]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useAsyncEffect(async () => {
    // Initialize search from URL on first load
    const urlSearch = getUrlSearchParam('search');
    if (urlSearch && urlSearch !== queryParams.search) {
      setSearchDisplay(urlSearch);
      setQueryParams(prev => ({ ...prev, search: urlSearch }));
    }
    setHasInitialLoad(true);
  }, []);

  // Pagination handler
  const handlePaginationChange = useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    setQueryParams(prev => ({
      ...prev,
      page: pagination.pageIndex + 1, // Convert 0-based to 1-based
      limit: pagination.pageSize
    }));
  }, [setQueryParams]);

  // Sorting handler
  const handleSortingChange = useCallback((updaterOrValue: any) => {
    setQueryParams(prev => {
      const newSorting = typeof updaterOrValue === 'function'
        ? updaterOrValue(prev.sorting || [])
        : updaterOrValue;
      return {
        ...prev,
        page: 1, // Reset to first page on sort change
        sorting: newSorting
      };
    });
  }, [setQueryParams]);

  const getStatusBadge = (status?: ApplicantStatus) => {
    const statusConfig = {
      inReview: { variant: 'info' as const, label: L.admin_panel.applicants.all_applicants.table.statuses.reviewing },
      accepted: { variant: 'success' as const, label: L.admin_panel.applicants.all_applicants.table.statuses.accepted },
      rejected: { variant: 'error' as const, label: L.admin_panel.applicants.all_applicants.table.statuses.rejected },
      fraud: { variant: 'warning' as const, label: L.admin_panel.applicants.all_applicants.table.statuses.fraud }
    };

    if (!status) {
      return <Badge variant="neutral">{L.admin_panel.applicants.all_applicants.table.statuses.pending}</Badge>;
    }

    const config = statusConfig[status];

    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const APPLICANTS_COLUMNS: ColumnDef<Applicant>[] = React.useMemo(() => [
    {
      id: 'uid',
      accessorKey: 'uid',
      header: L.admin_panel.applicants.all_applicants.table.columns.uid,
      enableSorting: false,
      cell: ({ row }) => {
        const uid = row.original.uid;
        return (
          <CodeDisplay displayMode="inline" code={String(uid)} copyable={true} className="text-xs" />
        );
      }
    },
    {
      id: 'legal_name',
      accessorKey: 'legal_name',
      header: L.admin_panel.applicants.all_applicants.table.columns.legal_name,
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-gray-900">
          {(searchDisplay && searchDisplay === (queryParams.search || ''))
            ? highlightText(row.original.legal_name || '', searchDisplay)
            : (row.original.legal_name || '')}
        </span>
      )
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: L.admin_panel.applicants.all_applicants.table.columns.review_state,
      enableSorting: false,
      cell: ({ row }) => getStatusBadge(row.original.status)
    },
    {
      id: 'actions',
      header: L.admin_panel.applicants.all_applicants.table.columns.actions,
      enableSorting: false,
      cell: ({ row }) => {
        // TODO: update to add subsurveys
        return <Button href={'/admin-panel/a/'+row.original.uid} variant="link" openInNewWindow>
          <div className="flex gap-2 items-center">
            {L.admin_panel.applicants.all_applicants.table.actions.view}
            <Icon size="sm" role="decorative" name="ArrowTopRightOnSquareIcon" />
          </div>
        </Button>
      }
    }
  ], [searchDisplay, queryParams.search, L]);

  const applicantsEmptyState = (
    <div className="py-8 text-center text-gray-600">
      <div>
        {L.admin_panel.applicants.all_applicants.table.empty_state}
      </div>
    </div>
  );

  // Check if filters are active (either in local state or in query params being sent to API)
  const hasActiveFilters = (filterConfig?.enabled && hasAnyFilters(filterConfig)) || !!queryParams.viewFilters;

  // Only show empty state if we have completed initial load, have no applicants,
  // no active search, no active filters, and are not currently loading/fetching
  // Note: We check both filterConfig AND queryParams.viewFilters to handle the transition
  // when filters are being cleared but data hasn't refreshed yet
  const applicantsEmpty =
    hasInitialLoad &&
    !isApplicantsLoading &&
    !isFetching &&
    (!applicantsData || !applicantsData.applicants || applicantsData.applicants.length === 0) &&
    !queryParams.search.trim() &&
    !hasActiveFilters
  // Keyboard shortcuts for pagination
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when not typing in an input/textarea
      if (event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement) {
        return;
      }

      if (!applicantsData) return;

      // Handle Alt+ArrowLeft for previous page
      if (matchesExactKey(event, { key: 'ArrowLeft', alt: true }) && queryParams.page > 1) {
        event.preventDefault();
        handlePaginationChange({
          pageIndex: queryParams.page - 2, // Convert to 0-based index
          pageSize: queryParams.limit
        });
      }

      // Handle Alt+ArrowRight for next page
      if (matchesExactKey(event, { key: 'ArrowRight', alt: true }) && applicantsData.totalPages && queryParams.page < applicantsData.totalPages) {
        event.preventDefault();
        handlePaginationChange({
          pageIndex: queryParams.page, // Already correct for 0-based index
          pageSize: queryParams.limit
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [queryParams.page, applicantsData?.totalPages, queryParams.limit, handlePaginationChange, applicantsData]);

  return (
    <Card className="rounded-md h-full w-full">
      <CardBody>
        {applicantsEmpty ? (
          applicantsEmptyState
        ) : (
          <div className="relative">
            <DataTable
              enableColumnVisibility
              columns={APPLICANTS_COLUMNS}
              data={applicantsData?.applicants || []}
              enableSearch
              searchLabel={L.admin_panel.applicants.all_applicants.table.search_label}
              dataFilteredOutMessage={
                searchDisplay.trim()
                  ? L.admin_panel.applicants.all_applicants.table.no_matches.replace('{search}', searchDisplay)
                  : hasActiveFilters
                    ? 'No applicants match the current filters'
                    : L.admin_panel.applicants.all_applicants.table.empty_state
              }
              initializing={!hasInitialLoad} // Show shimmer state during initial load
              loading={showLoadingSpinner}
              showPagination
              pageSize={queryParams.limit}
              filters={() => (
                availableFields.length > 0 && (
                  <FilterPanel
                    inline
                    {...filterPanelProps}
                    onConfirm={handleFilterChange}
                    isReloading={isFetching}
                    fetchFieldValues={fetchSuggestions}
                  />
                )
              )}
              activeFilters={filterConfig?.enabled && hasAnyFilters(filterConfig) ? (filterConfig.config?.conditions?.length || 0) : 0}
              manualPagination={{
                pageCount: applicantsData?.totalPages || 0,
                pageIndex: Math.max(0, queryParams.page - 1), // Convert from 1-based to 0-based
                totalRows: applicantsData?.total || 0,
                onPaginationChange: handlePaginationChange
              }}
              manualSorting={{
                sorting: queryParams.sorting || [],
                onSortingChange: handleSortingChange
              }}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};
